<template>
	<div class="sip-time-picker" :class="{ 'is-open': open }" ref="root">
		<div class="sip-time-picker__bar" role="group" aria-label="Time">
			<button
				type="button"
				class="sip-time-picker__clock-btn"
				:class="{ 'is-active': open }"
				:aria-expanded="open ? 'true' : 'false'"
				aria-haspopup="dialog"
				:title="trans('general.select') || 'Pick time'"
				@click.stop="toggleClock"
			>
				<em class="icon ni ni-clock" aria-hidden="true"></em>
			</button>

			<select
				:value="hour"
				class="custom-select sip-time-picker__field sip-time-picker__field--hour"
				name="hour"
				aria-label="Hour"
				@change="updateHour"
			>
				<option value="">{{trans('general.select_hour')}}</option>
				<option v-for="hr in hours" :key="'h'+hr" :value="hr">{{ timePadding(hr) }}</option>
			</select>

			<span class="sip-time-picker__sep" aria-hidden="true">:</span>

			<select
				:value="minute"
				class="custom-select sip-time-picker__field sip-time-picker__field--minute"
				name="minute"
				aria-label="Minute"
				@change="updateMinute"
			>
				<option value="">{{trans('general.select_minute')}}</option>
				<option v-for="min in mins" :key="'m'+min" :value="min">{{ timePadding(min) }}</option>
			</select>

			<div class="sip-time-picker__meridiem" role="group" aria-label="AM/PM">
				<button
					type="button"
					class="sip-time-picker__meridiem-btn"
					:class="{ 'is-active': normalizedMeridiem === 'am' }"
					@click="setMeridiem('am')"
				>AM</button>
				<button
					type="button"
					class="sip-time-picker__meridiem-btn"
					:class="{ 'is-active': normalizedMeridiem === 'pm' }"
					@click="setMeridiem('pm')"
				>PM</button>
			</div>
		</div>

		<div
			v-if="open"
			class="sip-time-picker__popover"
			role="dialog"
			aria-label="Clock"
			@click.stop
		>
			<div class="sip-time-picker__popover-head">
				<button
					type="button"
					class="sip-time-picker__display-part"
					:class="{ 'is-active': clockMode === 'hour' }"
					@click="clockMode = 'hour'"
				>{{ displayHour }}</button>
				<span class="sip-time-picker__display-sep">:</span>
				<button
					type="button"
					class="sip-time-picker__display-part"
					:class="{ 'is-active': clockMode === 'minute' }"
					@click="clockMode = 'minute'"
				>{{ displayMinute }}</button>
				<div class="sip-time-picker__popover-meridiem">
					<button
						type="button"
						class="sip-time-picker__meridiem-btn"
						:class="{ 'is-active': normalizedMeridiem === 'am' }"
						@click="setMeridiem('am')"
					>AM</button>
					<button
						type="button"
						class="sip-time-picker__meridiem-btn"
						:class="{ 'is-active': normalizedMeridiem === 'pm' }"
						@click="setMeridiem('pm')"
					>PM</button>
				</div>
			</div>

			<div class="sip-time-picker__face" @mousedown.prevent>
				<svg class="sip-time-picker__svg" viewBox="0 0 240 240" aria-hidden="true">
					<circle class="sip-time-picker__dial" cx="120" cy="120" r="110" />
					<line
						class="sip-time-picker__hand"
						x1="120"
						y1="120"
						:x2="hand.x"
						:y2="hand.y"
					/>
					<circle class="sip-time-picker__hub" cx="120" cy="120" r="5" />
					<g
						v-for="tick in faceTicks"
						:key="clockMode + '-' + tick.value"
						class="sip-time-picker__tick"
						:class="{ 'is-active': tick.active }"
						@click="pickTick(tick.value)"
					>
						<circle
							class="sip-time-picker__tick-dot"
							:cx="tick.x"
							:cy="tick.y"
							r="16"
						/>
						<text
							class="sip-time-picker__tick-label"
							:x="tick.x"
							:y="tick.y"
							dominant-baseline="central"
							text-anchor="middle"
						>{{ tick.label }}</text>
					</g>
				</svg>
			</div>

			<div class="sip-time-picker__popover-foot">
				<button type="button" class="sip-time-picker__link" @click="setNow">Now</button>
				<button type="button" class="sip-time-picker__done" @click="closeClock">Done</button>
			</div>
		</div>
	</div>
</template>

<script>
	export default {
		props: ['hour', 'minute', 'meridiem'],
		data() {
			return {
				open: false,
				clockMode: 'hour',
				hours: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
				mins: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59],
				minuteSteps: [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55]
			};
		},
		computed: {
			normalizedMeridiem() {
				const m = String(this.meridiem || 'am').toLowerCase();
				return m === 'pm' ? 'pm' : 'am';
			},
			activeHour() {
				const h = parseInt(this.hour, 10);
				return (h >= 1 && h <= 12) ? h : 12;
			},
			activeMinute() {
				const m = parseInt(this.minute, 10);
				return (m >= 0 && m <= 59) ? m : 0;
			},
			displayHour() {
				return this.hour === '' || this.hour === null || typeof this.hour === 'undefined'
					? '--'
					: this.timePadding(this.hour);
			},
			displayMinute() {
				return this.minute === '' || this.minute === null || typeof this.minute === 'undefined'
					? '--'
					: this.timePadding(this.minute);
			},
			faceTicks() {
				const values = this.clockMode === 'hour' ? this.hours : this.minuteSteps;
				const active = this.clockMode === 'hour' ? this.activeHour : this.nearestMinuteStep(this.activeMinute);
				return values.map((value, index) => {
					const angle = this.clockMode === 'hour'
						? (value % 12) * 30
						: (value / 5) * 30;
					const pos = this.pointOnCircle(angle, 82);
					return {
						value,
						label: this.clockMode === 'hour' ? String(value) : this.timePadding(value),
						active: Number(active) === Number(value),
						x: pos.x,
						y: pos.y,
						index
					};
				});
			},
			hand() {
				const angle = this.clockMode === 'hour'
					? (this.activeHour % 12) * 30
					: this.activeMinute * 6;
				return this.pointOnCircle(angle, 70);
			}
		},
		mounted() {
			document.addEventListener('click', this.onDocClick, true);
			document.addEventListener('keydown', this.onKeydown, true);
		},
		beforeDestroy() {
			document.removeEventListener('click', this.onDocClick, true);
			document.removeEventListener('keydown', this.onKeydown, true);
		},
		methods: {
			timePadding(time) {
				return helper.formatWithPadding(time, 2);
			},
			pointOnCircle(angleDeg, radius) {
				const rad = ((angleDeg - 90) * Math.PI) / 180;
				return {
					x: 120 + radius * Math.cos(rad),
					y: 120 + radius * Math.sin(rad)
				};
			},
			nearestMinuteStep(minute) {
				let best = this.minuteSteps[0];
				let bestDiff = 60;
				this.minuteSteps.forEach((step) => {
					const diff = Math.abs(step - minute);
					if (diff < bestDiff) {
						best = step;
						bestDiff = diff;
					}
				});
				return best;
			},
			updateHour(event) {
				this.$emit('update:hour', event.target.value);
			},
			updateMinute(event) {
				this.$emit('update:minute', event.target.value);
			},
			setMeridiem(value) {
				this.$emit('update:meridiem', value);
			},
			toggleClock() {
				this.open = !this.open;
				if (this.open) {
					if (this.hour === '' || this.hour === null || typeof this.hour === 'undefined') {
						this.clockMode = 'hour';
					} else if (this.minute === '' || this.minute === null || typeof this.minute === 'undefined') {
						this.clockMode = 'minute';
					} else {
						this.clockMode = 'hour';
					}
				}
			},
			closeClock() {
				this.open = false;
			},
			onDocClick(event) {
				if (!this.open || !this.$refs.root) {
					return;
				}
				if (!this.$refs.root.contains(event.target)) {
					this.closeClock();
				}
			},
			onKeydown(event) {
				if (this.open && event.key === 'Escape') {
					this.closeClock();
				}
			},
			pickTick(value) {
				if (this.clockMode === 'hour') {
					this.$emit('update:hour', String(value));
					this.clockMode = 'minute';
					if (this.minute === '' || this.minute === null || typeof this.minute === 'undefined') {
						this.$emit('update:minute', '0');
					}
					return;
				}
				this.$emit('update:minute', String(value));
			},
			setNow() {
				const now = new Date();
				let h = now.getHours();
				const m = now.getMinutes();
				const meridiem = h >= 12 ? 'pm' : 'am';
				h = h % 12;
				if (h === 0) {
					h = 12;
				}
				this.$emit('update:hour', String(h));
				this.$emit('update:minute', String(m));
				this.$emit('update:meridiem', meridiem);
				this.clockMode = 'hour';
			}
		}
	};
</script>

<style>
/* Works outside .nk-app-root (drawers append to body) */
.sip-time-picker {
	position: relative;
	display: block;
	width: 100%;
}

.sip-time-picker__bar {
	display: flex;
	flex-wrap: nowrap;
	align-items: stretch;
	width: 100%;
	min-height: var(--sip-input-h, 42px);
	height: var(--sip-input-h, 42px);
	overflow: hidden;
	border: 1px solid var(--sip-line, #e2e8f0);
	border-radius: var(--sip-input-radius, 12px);
	background: var(--sip-surface, #fff);
	box-sizing: border-box;
	transition: border-color 0.2s var(--sip-ease, ease), box-shadow 0.2s var(--sip-ease, ease);
}

.sip-time-picker__bar:focus-within,
.sip-time-picker.is-open .sip-time-picker__bar {
	border-color: color-mix(in srgb, var(--sip-accent, #0284c7) 55%, var(--sip-line, #e2e8f0));
	box-shadow: var(--sip-focus-ring, 0 0 0 3px color-mix(in srgb, var(--sip-accent, #0284c7) 18%, transparent));
}

.sip-time-picker__clock-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex: 0 0 40px;
	width: 40px;
	margin: 0;
	padding: 0;
	border: 0;
	border-right: 1px solid var(--sip-line, #e2e8f0);
	border-radius: 0;
	background: color-mix(in srgb, var(--sip-accent, #0284c7) 8%, transparent);
	color: var(--sip-accent, #0284c7);
	cursor: pointer;
	transition: background 0.15s ease, color 0.15s ease;
}

.sip-time-picker__clock-btn .icon {
	font-size: 1.05rem;
	line-height: 1;
}

.sip-time-picker__clock-btn:hover,
.sip-time-picker__clock-btn.is-active {
	background: color-mix(in srgb, var(--sip-accent, #0284c7) 16%, transparent);
	color: var(--sip-accent-hover, #0369a1);
}

.sip-time-picker__field,
html body .sip-drawer-body .sip-time-picker .custom-select,
html body .sip-drawer-body .sip-time-picker .sip-time-picker__field,
html body .doc-bottom-sheet-body .sip-time-picker .custom-select,
html body .modal.show .modal-body .sip-time-picker .custom-select,
html body .modal.d-block .modal-body .sip-time-picker .custom-select,
.nk-app-root .sip-time-picker .custom-select,
.nk-app-root .form-group .sip-time-picker .custom-select {
	flex: 1 1 0 !important;
	width: auto !important;
	min-width: 0 !important;
	max-width: none !important;
	height: 100% !important;
	min-height: 0 !important;
	margin: 0 !important;
	padding: 0 1.55rem 0 0.55rem !important;
	border: 0 !important;
	border-radius: 0 !important;
	background-color: transparent !important;
	background-image: linear-gradient(45deg, transparent 50%, var(--sip-muted, #64748b) 50%),
		linear-gradient(135deg, var(--sip-muted, #64748b) 50%, transparent 50%) !important;
	background-position: calc(100% - 12px) calc(50% - 2px), calc(100% - 7px) calc(50% - 2px) !important;
	background-size: 5px 5px, 5px 5px !important;
	background-repeat: no-repeat !important;
	box-shadow: none !important;
	color: var(--sip-text, #0f172a) !important;
	font-weight: 600 !important;
	appearance: none;
	-webkit-appearance: none;
}

.sip-time-picker__field:focus,
.sip-time-picker__field:focus-visible,
html body .sip-drawer-body .sip-time-picker .custom-select:focus,
html body .sip-drawer-body .sip-time-picker .custom-select:focus-visible {
	outline: none !important;
	box-shadow: none !important;
	border: 0 !important;
}

.sip-time-picker__sep {
	display: flex;
	align-items: center;
	flex: 0 0 auto;
	padding: 0 2px;
	color: var(--sip-muted, #64748b);
	font-weight: 700;
	font-variant-numeric: tabular-nums;
	line-height: 1;
	user-select: none;
}

.sip-time-picker__meridiem {
	display: flex;
	flex: 0 0 auto;
	align-items: stretch;
	margin: 3px;
	padding: 2px;
	border-radius: calc(var(--sip-input-radius, 12px) - 4px);
	background: color-mix(in srgb, var(--sip-bg, #f8fafc) 90%, var(--sip-line, #e2e8f0));
	gap: 2px;
}

.sip-time-picker__meridiem-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 2.15rem;
	margin: 0;
	padding: 0 0.45rem;
	border: 0;
	border-radius: calc(var(--sip-input-radius, 12px) - 6px);
	background: transparent;
	color: var(--sip-muted, #64748b);
	font-family: var(--sip-font, inherit);
	font-size: 0.72rem;
	font-weight: 700;
	letter-spacing: 0.03em;
	line-height: 1;
	cursor: pointer;
	transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.sip-time-picker__meridiem-btn.is-active {
	background: var(--sip-accent, #0284c7);
	color: #fff;
	box-shadow: 0 1px 3px color-mix(in srgb, var(--sip-accent, #0284c7) 35%, transparent);
}

.sip-time-picker__popover {
	position: absolute;
	z-index: 2400;
	top: calc(100% + 8px);
	left: 0;
	width: min(100%, 280px);
	padding: 12px;
	border: 1px solid var(--sip-line, #e2e8f0);
	border-radius: 14px;
	background: var(--sip-surface, #fff);
	box-shadow: 0 16px 40px rgba(15, 23, 42, 0.18);
	box-sizing: border-box;
}

.sip-time-picker__popover-head {
	display: flex;
	align-items: center;
	gap: 2px;
	margin-bottom: 10px;
}

.sip-time-picker__display-part {
	margin: 0;
	padding: 0 4px;
	border: 0;
	border-radius: 8px;
	background: transparent;
	color: var(--sip-muted, #64748b);
	font-family: var(--sip-font-display, var(--sip-font, inherit));
	font-size: 1.55rem;
	font-weight: 700;
	font-variant-numeric: tabular-nums;
	line-height: 1.1;
	cursor: pointer;
}

.sip-time-picker__display-part.is-active {
	color: var(--sip-accent, #0284c7);
	background: color-mix(in srgb, var(--sip-accent, #0284c7) 12%, transparent);
}

.sip-time-picker__display-sep {
	color: var(--sip-muted, #64748b);
	font-size: 1.45rem;
	font-weight: 700;
	line-height: 1;
}

.sip-time-picker__popover-meridiem {
	display: flex;
	align-items: stretch;
	align-self: center;
	margin-left: auto;
	height: 32px;
	padding: 2px;
	border-radius: 10px;
	background: color-mix(in srgb, var(--sip-bg, #f8fafc) 90%, var(--sip-line, #e2e8f0));
	box-sizing: border-box;
	gap: 2px;
}

.sip-time-picker__popover-meridiem .sip-time-picker__meridiem-btn {
	min-width: 2.35rem;
	min-height: 28px;
	height: 100%;
	padding: 0 0.7rem;
	border-radius: 8px;
	font-size: 0.75rem;
}

.sip-time-picker__face {
	display: flex;
	justify-content: center;
	padding: 4px 0 8px;
}

.sip-time-picker__svg {
	width: 220px;
	height: 220px;
	max-width: 100%;
	user-select: none;
}

.sip-time-picker__dial {
	fill: color-mix(in srgb, var(--sip-accent, #0284c7) 8%, var(--sip-bg, #f8fafc));
}

.sip-time-picker__hand {
	stroke: var(--sip-accent, #0284c7);
	stroke-width: 2.5;
	stroke-linecap: round;
}

.sip-time-picker__hub {
	fill: var(--sip-accent, #0284c7);
}

.sip-time-picker__tick {
	cursor: pointer;
}

.sip-time-picker__tick-dot {
	fill: transparent;
	transition: fill 0.12s ease;
}

.sip-time-picker__tick:hover .sip-time-picker__tick-dot {
	fill: color-mix(in srgb, var(--sip-accent, #0284c7) 16%, transparent);
}

.sip-time-picker__tick.is-active .sip-time-picker__tick-dot {
	fill: var(--sip-accent, #0284c7);
}

.sip-time-picker__tick-label {
	fill: var(--sip-text, #0f172a);
	font-family: var(--sip-font, inherit);
	font-size: 13px;
	font-weight: 650;
	pointer-events: none;
}

.sip-time-picker__tick.is-active .sip-time-picker__tick-label {
	fill: #fff;
}

.sip-time-picker__popover-foot {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	padding-top: 4px;
}

.sip-time-picker__link,
.sip-time-picker__done {
	margin: 0;
	border: 0;
	border-radius: 8px;
	font-family: var(--sip-font, inherit);
	font-size: 0.8rem;
	font-weight: 650;
	cursor: pointer;
}

.sip-time-picker__link {
	padding: 6px 8px;
	background: transparent;
	color: var(--sip-accent, #0284c7);
}

.sip-time-picker__done {
	padding: 6px 12px;
	background: var(--sip-accent, #0284c7);
	color: #fff;
}

html.dark-mode .sip-time-picker__bar,
body.dark-mode .sip-time-picker__bar {
	background: var(--sip-surface, #1e293b);
	border-color: var(--sip-line, #334155);
}

html.dark-mode .sip-time-picker__clock-btn,
body.dark-mode .sip-time-picker__clock-btn {
	border-right-color: var(--sip-line, #334155);
	background: color-mix(in srgb, var(--sip-accent, #0284c7) 14%, transparent);
	color: var(--sip-accent-on, #38bdf8);
}

html.dark-mode .sip-time-picker__field,
html.dark-mode .sip-time-picker .custom-select,
body.dark-mode .sip-time-picker__field,
body.dark-mode .sip-time-picker .custom-select {
	color: var(--sip-text, #e2e8f0) !important;
	background-color: transparent !important;
}

html.dark-mode .sip-time-picker__meridiem,
html.dark-mode .sip-time-picker__popover-meridiem,
body.dark-mode .sip-time-picker__meridiem,
body.dark-mode .sip-time-picker__popover-meridiem {
	background: color-mix(in srgb, var(--sip-bg, #0f172a) 75%, var(--sip-line, #334155));
}

html.dark-mode .sip-time-picker__popover,
body.dark-mode .sip-time-picker__popover {
	background: var(--sip-surface, #1e293b);
	border-color: var(--sip-line, #334155);
	box-shadow: 0 18px 44px rgba(0, 0, 0, 0.45);
}

html.dark-mode .sip-time-picker__dial,
body.dark-mode .sip-time-picker__dial {
	fill: color-mix(in srgb, var(--sip-accent, #0284c7) 14%, var(--sip-bg, #0f172a));
}

html.dark-mode .sip-time-picker__tick-label,
body.dark-mode .sip-time-picker__tick-label {
	fill: var(--sip-text, #e2e8f0);
}
</style>
